// API service for calling the standalone BIKI Azure Functions app.
// API_BASE is absolute in Azure and relative in local dev (Vite proxies /api).

import { msalInstance, loginRequest, tokenRequest } from '../lib/auth';
import { API_BASE } from './apiBase';

interface ApiResponse<T> {
    data: T;
    error?: string;
}

/**
 * Access token for BIKI's own API.
 *
 * The scope matters: a token for `User.Read` is audienced to Microsoft Graph
 * and the API rejects it. On a silent failure the user is sent through an
 * interactive redirect, which is the normal path when a refresh token has
 * expired or consent is needed.
 */
export async function getAuthToken(): Promise<string | null> {
    const account = msalInstance.getAllAccounts()[0];
    if (!account) return null;

    try {
        const result = await msalInstance.acquireTokenSilent({
            account,
            scopes: tokenRequest.scopes,
        });
        return result.accessToken;
    } catch {
        try {
            await msalInstance.acquireTokenRedirect({ scopes: tokenRequest.scopes });
        } catch {
            // Redirect itself failed; let the caller surface the 401.
        }
        return null;
    }
}

// Base fetch wrapper with auth
async function fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    const token = await getAuthToken();

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
    };

    // The bearer token is the only identity the API accepts. The app used to
    // also send X-User-Principal-Name and X-ObjectId, which the API trusted -
    // so anyone could claim to be anyone. They are gone, and the API now reads
    // identity from the principal Entra validated.
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const errorText = await response.text();
        return {
            data: null as unknown as T,
            error: `${response.status}: ${errorText || response.statusText}`,
        };
    }

    const data = await response.json();
    return { data, error: undefined };
}

// ==================== Release API ====================

export const releaseApi = {
    getAll: () => fetchApi<any[]>('/releases'),
    getById: (id: string) => fetchApi(`/releases/${id}`),
    create: (dto: { version: string; plannedReleaseDate?: string; actualReleaseDate?: string }) =>
        fetchApi('/releases', {
            method: 'POST',
            body: JSON.stringify({
                version: dto.version,
                plannedReleaseDate: dto.plannedReleaseDate,
                actualReleaseDate: dto.actualReleaseDate,
            }),
        }),
    update: (id: string, dto: Partial<{ version: string; plannedReleaseDate: string; actualReleaseDate: string; isActive: boolean }>) =>
        fetchApi(`/releases/${id}`, {
            method: 'PUT',
            body: JSON.stringify(dto),
        }),
    delete: (id: string) => fetchApi(`/releases/${id}`, { method: 'DELETE' }),
};

// ==================== Release Note API ====================

/** A note found by its Jira key, with the releases it belongs to. */
export interface ReleaseNoteByKey {
    id: string;
    jiraKey: string;
    title: string;
    /**
     * Most useful first - active releases before inactive, soonest planned
     * first - so taking the head opens the release the note is most likely
     * wanted in. Empty when the issue is in no release yet.
     */
    releases: { id: string; version: string; isActive: boolean; plannedReleaseDate: string | null }[];
}

export const releaseNoteApi = {
    /**
     * Resolves a Jira key, for /releases/BIF-87.
     *
     * A key the caller may not see answers the same as one that does not
     * exist, so a 404 here means "nothing to show", never "exists but not
     * yours".
     */
    getByKey: (jiraKey: string) =>
        fetchApi<ReleaseNoteByKey>(`/releasenotes/key/${encodeURIComponent(jiraKey)}`),
    search: (query: string) => {
        const params = new URLSearchParams({ q: query });
        return fetchApi<any[]>(`/releasenotes/search?${params.toString()}`);
    },
    getAll: (releaseId?: string, packageFilter?: string) => {
        let url = '/releasenotes';
        const params = new URLSearchParams();
        if (releaseId) params.set('releaseId', releaseId);
        if (packageFilter) params.set('package', packageFilter);
        if (params.toString()) url += `?${params.toString()}`;
        return fetchApi<any[]>(url);
    },
    getById: (id: string) => fetchApi(`/releasenotes/${id}`),
    create: (dto: { releaseId: string; jiraKey?: string; title?: string; description?: string; package?: string; relatedArticleIds?: string[] }) =>
        fetchApi('/releasenotes', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    update: (id: string, dto: Partial<{ jiraKey: string; title: string; description: string; package: string; relatedArticleIds: string[] }>) =>
        fetchApi(`/releasenotes/${id}`, {
            method: 'PUT',
            body: JSON.stringify(dto),
        }),
    delete: (id: string) => fetchApi(`/releasenotes/${id}`, { method: 'DELETE' }),
};

// ==================== Wiki Article API ====================

export const wikiArticleApi = {
    getAll: (params?: { query?: string; tag?: string; tocId?: string; isPublished?: boolean }) => {
        let url = '/wiki/articles';
        const searchParams = new URLSearchParams();
        if (params?.query) searchParams.set('query', params.query);
        if (params?.tag) searchParams.set('tag', params.tag);
        if (params?.tocId) searchParams.set('tocId', params.tocId);
        if (params?.isPublished !== undefined) searchParams.set('isPublished', String(params.isPublished));
        if (searchParams.toString()) url += `?${searchParams.toString()}`;
        return fetchApi<any[]>(url);
    },
    getById: (id: string) => fetchApi(`/wiki/articles/${id}`),
    create: (dto: { tocId: string; title: string; content: string; tags?: string; isPublished?: boolean; createdBy?: string; updatedBy?: string }) =>
        fetchApi('/wiki/articles', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    update: (id: string, dto: Partial<{ title: string; content: string; tags: string; isPublished: boolean; tocId: string }>) =>
        fetchApi(`/wiki/articles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(dto),
        }),
    delete: (id: string) => fetchApi(`/wiki/articles/${id}`, { method: 'DELETE' }),
    search: (query: string, tag?: string) => {
        let url = '/wiki/articles/search';
        const params = new URLSearchParams({ q: query });
        if (tag) params.set('tag', tag);
        url += `?${params.toString()}`;
        return fetchApi<any[]>(url);
    },
};

// ==================== Wiki TOC API ====================

export const wikiTocApi = {
    getAll: () => fetchApi<any[]>('/wiki/toc'),
    getById: (id: string) => fetchApi(`/wiki/toc/${id}`),
    create: (dto: { parentId?: string; description: string; summary?: string; sortOrder?: number }) =>
        fetchApi('/wiki/toc', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    update: (id: string, dto: Partial<{ parentId: string; description: string; summary: string; sortOrder: number }>) =>
        fetchApi(`/wiki/toc/${id}`, {
            method: 'PUT',
            body: JSON.stringify(dto),
        }),
    delete: (id: string) => fetchApi(`/wiki/toc/${id}`, { method: 'DELETE' }),
};

// ==================== Wiki Backup API ====================

/** One stored archive, as listed by GET /api/wiki/backups (newest first). */
export interface WikiBackupEntry {
    name: string;
    sizeBytes: number;
    /** ISO-8601 UTC instant the blob was written. */
    created: string;
    /**
     * The environment the archive was taken in, read from its name. Null only
     * for a name that could not have come from a backup.
     *
     * Every environment's archives share one container - that is what lets
     * development restore live content - so this is how one is told from
     * another.
     */
    sourceEnvironment: string | null;
    /**
     * Whether this environment may delete it: production may delete any
     * archive, everywhere else only its own.
     *
     * Decided by the API, which enforces the same rule on the delete endpoint.
     * The screen renders this rather than working it out, so there is only one
     * copy of the rule.
     */
    canDelete: boolean;
}

/**
 * What a freshly created backup turned out to contain.
 *
 * `missingImages` and `droppedArticleRefs` are the counts of what could *not*
 * be captured: articles referencing images that are already gone from storage,
 * and release-note references pointing at articles outside the backup. The API
 * returns them deliberately rather than swallowing them, so the UI shows them.
 */
export interface WikiBackupSummary {
    name: string;
    createdUtc: string;
    sizeBytes: number;
    sections: number;
    articles: number;
    images: number;
    missingImages: number;
    releaseNoteLinks: number;
    droppedArticleRefs: number;
    message: string;
}

/** How a restore treats content the archive does not contain. */
export type RestoreMode = 'merge' | 'replace';

/** Sections or articles: what a restore would create, update and leave alone. */
export interface RestoreChangeSet {
    create: number;
    update: number;
    unchanged: number;
    /** Labels, not ids - "Parent / Child" for sections, "Section / Title" for articles. */
    toCreate: string[];
    toUpdate: string[];
}

export interface RestoreImageSet {
    reuseSameName: number;
    reuseByOriginalName: number;
    upload: number;
    toUpload: string[];
    /** Images the archive references but does not contain; these stay broken. */
    brokenReferences: string[];
}

export interface RestoreNoteLinkSet {
    notes: number;
    /** Notes in the archive that this environment does not have. */
    notFound: number;
    /** Notes whose (release, Jira key) pair matches more than one row here. */
    ambiguous: number;
    referencesMapped: number;
    referencesUnmappable: number;
    detail: string[];
}

/** Only ever populated in replace mode; merge deletes nothing. */
export interface RestoreDeletionSet {
    sections: string[];
    articles: string[];
    /** Sections that could not be deleted, with the reason. */
    blocked: string[];
    noteReferencesCleared: string[];
}

/**
 * What a restore did, or - when `applied` is false - what it would do.
 *
 * The same shape comes back from a dry run and a real one, which is what lets
 * the page show a plan and then apply exactly that plan.
 */
export interface WikiRestoreOutcome {
    backup: string;
    /** The environment the archive came from; null if the manifest omits it. */
    sourceEnvironment: string | null;
    generated: string;
    targetEnvironment: string;
    mode: string;
    applied: boolean;
    safetyBackup: string | null;
    sections: RestoreChangeSet;
    articles: RestoreChangeSet;
    images: RestoreImageSet;
    releaseNoteLinks: RestoreNoteLinkSet;
    deletions: RestoreDeletionSet;
    warnings: string[];
    message: string;
}

/**
 * A restore result, or a refusal.
 *
 * `problems` is the per-item list a failed validation carries. A refusal means
 * nothing was attempted and the wiki is untouched, which is why it is kept
 * apart from `error` rather than flattened into one string.
 */
export interface RestoreResponse {
    data: WikiRestoreOutcome | null;
    error?: string;
    problems?: string[];
}

export const backupApi = {
    // Wiki Admin only, like the rest of this group. Expect several seconds:
    // the whole wiki is zipped server-side, images included.
    create: () => fetchApi<WikiBackupSummary>('/wiki/backup', { method: 'POST' }),
    list: () => fetchApi<WikiBackupEntry[]>('/wiki/backups'),
    /**
     * Fetches an archive as a blob.
     *
     * Not `fetchApi`, and deliberately not an `<a href>` either: the response
     * is a zip rather than JSON, and the endpoint requires the bearer token -
     * which an anchor cannot carry, so a plain link would just 401. The caller
     * turns this blob into an object URL to trigger the save.
     */
    download: async (name: string): Promise<{ data: Blob | null; error?: string }> => {
        const token = await getAuthToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${API_BASE}/wiki/backups/${encodeURIComponent(name)}`, {
            headers,
        });

        if (!response.ok) {
            const errorText = await response.text();
            return {
                data: null,
                error: `${response.status}: ${errorText || response.statusText}`,
            };
        }

        return { data: await response.blob(), error: undefined };
    },

    /**
     * Removes a stored archive.
     *
     * The account keeps soft-deleted blobs for seven days and forbids
     * permanent delete, so this is recoverable from Azure - not from BIKI,
     * which cannot list a soft-deleted blob. That is why the screen confirms
     * in place rather than demanding the name typed out, as replace does.
     */
    remove: (name: string) =>
        fetchApi<{ name: string; deleted: boolean }>(
            `/wiki/backups/${encodeURIComponent(name)}`, { method: 'DELETE' }),

    /**
     * Plans a restore, or performs one.
     *
     * Not `fetchApi`, because a refusal is not a generic failure: the API
     * answers a 400/403/404 with `{ error, problems }`, and `fetchApi` would
     * flatten that into a string with raw JSON in the middle of it. The page
     * needs the per-item problems to list them.
     *
     * `apply` defaults to false, matching the API: a plain call reports a plan
     * and writes nothing. Applying additionally requires `confirm` to equal
     * the backup's own name.
     */
    restore: async (
        name: string,
        options: { mode: RestoreMode; apply: boolean; confirm?: string },
    ): Promise<RestoreResponse> => {
        const token = await getAuthToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${API_BASE}/wiki/restore/${encodeURIComponent(name)}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                mode: options.mode,
                apply: options.apply,
                confirm: options.confirm,
            }),
        });

        const text = await response.text();
        let body: any = null;
        try {
            body = text ? JSON.parse(text) : null;
        } catch {
            // A non-JSON body means something other than the API answered -
            // Easy Auth returning its own 401 page, most likely.
            body = null;
        }

        if (!response.ok) {
            return {
                data: null,
                error: body?.error || text || response.statusText,
                problems: Array.isArray(body?.problems) ? body.problems : undefined,
            };
        }

        return { data: body as WikiRestoreOutcome, error: undefined };
    },
};

// ==================== Image API ====================

export const imageApi = {
    upload: async (file: File): Promise<{ url: string; fileName: string } | { error: string } | null> => {
        const formData = new FormData();
        formData.append('file', file);

        const token = await getAuthToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        headers['X-Filename'] = file.name;

        const response = await fetch(`${API_BASE}/images/upload`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            try {
                const errorData = JSON.parse(errorText);
                return { error: errorData.error || errorText };
            } catch {
                return { error: errorText || response.statusText };
            }
        }
        return response.json();
    },
    getImageUrl: (blobName: string) => `${API_BASE}/images/${blobName}`,
    deleteImage: (blobName: string) => fetchApi(`/images/${blobName}`, { method: 'DELETE' }),
    list: () => fetchApi<any[]>('/images'),
};

// ==================== Jira API ====================

export const jiraApi = {
    getProjects: () => fetchApi<any[]>('/jira/projects'),
    getVersions: (projectId: string) => fetchApi<any[]>(`/jira/projects/${projectId}/versions`),
    searchIssues: (jql: string, maxResults = 100, startAt = 0) => {
        const params = new URLSearchParams({ jql, maxResults: String(maxResults), startAt: String(startAt) });
        return fetchApi<any[]>(`/jira/issues/search?${params.toString()}`);
    },
    getIssue: (issueKey: string) => fetchApi(`/jira/issues/${issueKey}`),
    getIssuesByVersion: (version: string) => fetchApi<any[]>(`/jira/versions/${encodeURIComponent(version)}/issues`),
    syncToRelease: (dto: { version: string; jqlQuery?: string; issueKeys?: string[]; component?: string }) =>
        fetchApi<{ ReleaseId: string; Version: string; IssuesSynced: number; Message: string }>('/jira/sync', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    importIssue: (dto: { issueKey: string; releaseId: string; package?: string }) =>
        fetchApi('/jira/import', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    getComponents: (projectId: string) => fetchApi<any[]>(`/jira/projects/${projectId}/components`),
    getAssignees: (projectId: string) => fetchApi<any[]>(`/jira/projects/${projectId}/assignees`),
    syncVersions: () => fetchApi<any>('/jira/sync-versions', { method: 'POST' }),
};

// ==================== User Role API ====================

/** None < Reader < Contributor < Admin. */
export type AccessLevel = 'None' | 'Reader' | 'Contributor' | 'Admin';

export interface UserAccess {
    userPrincipalName: string;
    name: string | null;
    releaseNotes: AccessLevel;
    wiki: AccessLevel;
    isSystemAdmin: boolean;
    /** The customer this person belongs to, or null for Binary staff. */
    companyId: string | null;
    companyName: string | null;
}

/** A customer that release notes can be targeted at. */
export interface Company {
    id: string;
    name: string;
    /** The token identifying this company on a Jira issue. */
    jiraValue: string;
    /**
     * The Jira option this company is, or null when somebody created it by
     * hand. Present means the nightly sync maintains it.
     */
    jiraOptionId: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    /** People assigned to it, and notes targeted at it. */
    userCount: number;
    noteCount: number;
}

export const userRoleApi = {
    getAll: () => fetchApi<any[]>('/users/roles'),
    getByUpn: (upn: string) => fetchApi(`/users/roles/${encodeURIComponent(upn)}`),
    // Sent whole rather than as a patch: the admin page always states the
    // complete intended access for that person.
    assign: (dto: {
        userPrincipalName: string;
        releaseNotes: AccessLevel;
        wiki: AccessLevel;
        isSystemAdmin: boolean;
        // Sent explicitly, null included: no company is a real answer
        // (Binary staff, who see every note) rather than a missing one.
        companyId: string | null;
    }) =>
        fetchApi('/users/roles', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
    remove: (upn: string) => fetchApi(`/users/roles/${encodeURIComponent(upn)}`, { method: 'DELETE' }),
    // Answers about the signed-in caller only; the API derives the identity
    // from the validated token rather than a header the caller supplies, so it
    // cannot be asked about somebody else. It answers even when the caller has
    // no access, which is what renders the no-access screen.
    me: () => fetchApi<UserAccess>('/users/me'),
};

// ==================== Company API ====================

// System-admin only, like user management. The list names every customer
// Binary works with, so it is deliberately not readable by the customers the
// note filter is applied to.
export const companyApi = {
    getAll: () => fetchApi<Company[]>('/companies'),
    create: (dto: { name: string; jiraValue: string }) =>
        fetchApi<Company>('/companies', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: { name: string; jiraValue: string }) =>
        fetchApi<Company>(`/companies/${id}`, { method: 'PUT', body: JSON.stringify(dto) }),
    remove: (id: string) => fetchApi(`/companies/${id}`, { method: 'DELETE' }),
    // Builds the list from the Jira Customer field's options. The nightly
    // workflow calls the same endpoint; this is the same act on demand, for
    // the administrator who has just had a customer added in Jira and cannot
    // wait until tomorrow to put somebody in it.
    refreshFromJira: () =>
        fetchApi<CompanySyncResult>('/jira/sync-customers', { method: 'POST' }),
};

/** What sync-customers reports back. */
export interface CompanySyncResult {
    optionsSeen: number;
    companiesAdded?: number;
    companiesAdopted?: number;
    companiesRelabelled?: number;
    noLongerInJira?: string[];
    message?: string;
    /** Set instead of the counts when Jira offered nothing and it declined to act. */
    skipped?: string;
}

// ==================== Auth Helpers ====================

export const authHelpers = {
    getAuthToken,
    login: async () => {
        msalInstance.loginRedirect(loginRequest);
    },
    logout: async () => {
        msalInstance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        });
    },
};
